import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import Select, { SelectOption } from '@/components/select/Select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table/Table';
import { NotificationTemplateDto } from '@/services/api/contracts/adminNotifications';
import { 
  useCreateNotificationTemplate, 
  useGetNotificationTemplates,
  useUpdateNotificationTemplate,
  useDeleteNotificationTemplate,
  useGetNotificationEvents
} from '@/services/api/hooks/adminNotifications';
import { usePartners } from '@/services/api/hooks/partners';
import { useCompanies } from '@/services/api/hooks/companies';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Editor from '@monaco-editor/react';
import { Pencil, Trash } from 'lucide-react';

type ScopeType = 'Global' | 'MSP' | 'Customer';

const TemplateManager: FC = () => {
  const [templates] = useGetNotificationTemplates();
  const [events] = useGetNotificationEvents();
  const { mutateAsync: createTemplate } = useCreateNotificationTemplate();
  const { mutateAsync: updateTemplate } = useUpdateNotificationTemplate();
  const { mutateAsync: deleteTemplate } = useDeleteNotificationTemplate();

  const [msps] = usePartners();
  
  const [selectedScope, setSelectedScope] = useState<ScopeType>('Global');
  const [selectedMspId, setSelectedMspId] = useState<string | null>(null);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);

  // Fetch companies only if an MSP is selected and scope is Customer
  const [companies] = useCompanies(selectedMspId || '');

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting }
  } = useForm<NotificationTemplateDto>({
    resolver: zodResolver(NotificationTemplateDto)
  });

  const onSubmit = async (data: NotificationTemplateDto) => {
    // Override scope based on UI selection to prevent accidental scopes
    const payload: NotificationTemplateDto = {
      ...data,
      scopeMspId: selectedScope === 'MSP' || selectedScope === 'Customer' ? data.scopeMspId : null,
      scopeCompanyId: selectedScope === 'Customer' ? data.scopeCompanyId : null
    };

    if (editingTemplateId) {
      await updateTemplate({ id: editingTemplateId, template: payload });
    } else {
      await createTemplate(payload);
    }
    
    cancelEdit();
  };

  const handleEdit = (template: NotificationTemplateDto) => {
    setEditingTemplateId(template.id!);
    
    if (template.scopeCompanyId) {
      setSelectedScope('Customer');
      setSelectedMspId(template.scopeMspId || null);
    } else if (template.scopeMspId) {
      setSelectedScope('MSP');
      setSelectedMspId(template.scopeMspId);
    } else {
      setSelectedScope('Global');
      setSelectedMspId(null);
    }
    
    reset(template);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      await deleteTemplate(id);
    }
  };

  const cancelEdit = () => {
    setEditingTemplateId(null);
    setSelectedScope('Global');
    setSelectedMspId(null);
    reset({
      notificationEventId: '',
      senderName: '',
      senderEmail: '',
      subjectLiquid: '',
      bodyLiquid: '',
      scopeCompanyId: null,
      scopeMspId: null
    });
  };

  const scopeOptions: SelectOption[] = [
    { label: 'Global (Fallback)', value: 'Global' },
    { label: 'Specific MSP', value: 'MSP' },
    { label: 'Specific Customer', value: 'Customer' }
  ];

  const mspOptions: SelectOption[] = msps?.map((m: any) => ({ label: m.name, value: m.id })) || [];
  const companyOptions: SelectOption[] = companies?.map((c: any) => ({ label: c.name, value: c.id })) || [];
  const eventSelectOptions: SelectOption[] = events?.map((e: any) => ({ label: e.name, value: e.id })) || [];

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h4 className="text-xl font-bold mb-4">{editingTemplateId ? 'Edit Template' : 'Create New Template'}</h4>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-w-4xl">
          
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 mt-4">
            <div className="col-span-2">
              <h5 className="font-semibold mb-3">Target Scope</h5>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Scope Level</label>
              <Select
                options={scopeOptions}
                selectedValue={scopeOptions.find(o => o.value === selectedScope)}
                onChange={(val) => {
                  const option = val as SelectOption;
                  if (option) setSelectedScope(option.value as ScopeType);
                }}
              />
            </div>

            {(selectedScope === 'MSP' || selectedScope === 'Customer') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select MSP</label>
                <Controller
                  name="scopeMspId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      options={mspOptions}
                      selectedValue={mspOptions.find(o => o.value === field.value)}
                      onChange={(val) => {
                        const option = val as SelectOption;
                        if (option) {
                          field.onChange(option.value);
                          setSelectedMspId(option.value);
                        }
                      }}
                    />
                  )}
                />
              </div>
            )}

            {selectedScope === 'Customer' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Customer</label>
                <Controller
                  name="scopeCompanyId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      options={companyOptions}
                      selectedValue={companyOptions.find(o => o.value === field.value)}
                      onChange={(val) => {
                        const option = val as SelectOption;
                        if (option) field.onChange(option.value);
                      }}
                    />
                  )}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 mt-4">
            <div className="col-span-2">
              <h5 className="font-semibold mb-3">Template Content</h5>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notification Event</label>
              <Controller
                name="notificationEventId"
                control={control}
                render={({ field }) => (
                  <Select
                    options={eventSelectOptions}
                    selectedValue={eventSelectOptions.find(o => o.value === field.value)}
                    onChange={(val) => {
                      const option = val as SelectOption;
                      if (option) field.onChange(option.value);
                    }}
                  />
                )}
              />
              {errors.notificationEventId && <p className="mt-1 text-sm text-red-600">{errors.notificationEventId.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Sender Name (Optional)</label>
              <input
                type="text"
                {...register('senderName')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                placeholder="e.g. Sales Team"
              />
              {errors.senderName && <p className="mt-1 text-sm text-red-600">{errors.senderName.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Sender Email (Optional)</label>
              <input
                type="email"
                {...register('senderEmail')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                placeholder="e.g. sales@daasportal.app"
              />
              {errors.senderEmail && <p className="mt-1 text-sm text-red-600">{errors.senderEmail.message}</p>}
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">Subject (Liquid Syntax)</label>
              <input
                type="text"
                {...register('subjectLiquid')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm font-mono"
                placeholder="e.g. New Order Received: {{ purchaseOrder.poRef }}"
              />
              {errors.subjectLiquid && <p className="mt-1 text-sm text-red-600">{errors.subjectLiquid.message}</p>}
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">HTML Body (Liquid Syntax)</label>
              <p className="text-xs text-gray-500 mb-2">Tip: Ensure image tags have explicit width attributes for Outlook compatibility.</p>
              <div className="mt-1 border border-gray-300 rounded-md overflow-hidden">
                <Controller
                  name="bodyLiquid"
                  control={control}
                  render={({ field }) => (
                    <Editor
                      height="500px"
                      language="html"
                      theme="vs-light"
                      value={field.value || ''}
                      onChange={(value) => field.onChange(value)}
                      options={{
                        minimap: { enabled: false },
                        wordWrap: 'on',
                        formatOnPaste: true,
                      }}
                    />
                  )}
                />
              </div>
              {errors.bodyLiquid && <p className="mt-1 text-sm text-red-600">{errors.bodyLiquid.message}</p>}
            </div>
          </div>

          <div className="flex space-x-3 mt-4">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : (editingTemplateId ? 'Update Template' : 'Create Template')}
            </Button>
            {editingTemplateId && (
              <Button type="button" variant={ButtonVariant.Secondary} onClick={cancelEdit}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h4 className="text-xl font-bold mb-4">Existing Templates</h4>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Event</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates?.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-gray-500 py-4">
                  No templates found.
                </TableCell>
              </TableRow>
            )}
            {templates?.map((template: NotificationTemplateDto, idx: number) => {
              let scopeStr = 'Global';
              if (template.scopeCompanyId) scopeStr = 'Customer';
              else if (template.scopeMspId) scopeStr = 'MSP';

              const evt = events?.find(e => e.id === template.notificationEventId);
              const eventName = evt ? evt.name : `Unknown Event (${template.notificationEventId})`;

              return (
                <TableRow key={template.id || idx}>
                  <TableCell className="font-mono text-xs">{eventName}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {scopeStr}
                    </span>
                  </TableCell>
                  <TableCell>{template.subjectLiquid}</TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => handleEdit(template)}
                      className="text-gray-400 hover:text-primary-600 transition-colors p-1"
                      title="Edit"
                    >
                      <Pencil className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(template.id!)}
                      className="text-gray-400 hover:text-red-600 transition-colors p-1 ml-2"
                      title="Delete"
                    >
                      <Trash className="h-5 w-5" />
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default TemplateManager;
